import { Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useNMPClassificationTree } from "../../hooks/useNMPClassificationTree";
import {
	useSchemeSubmit,
	useSchemeUpdate,
} from "../../hooks/useSchemeDataEntry";
import {
	flattenClassificationTree,
	generateClassificationSchema,
	generateInitialValues,
	generateYupShape,
	initialNMPValues,
	NMPSchemaStep1,
	NMPSchemaStep6,
} from "./NMPFormSchema";
import { NMPClassification } from "./nmpClassificationTree.api";
import NMPClassificationStep from "./NMPClassificationStep";
import NMPStep1 from "./NMPStep1";
import NMPStep6 from "./NMPStep6";
import { NMPStepProgress } from "./NMPStepProgress";

type Props = {
	isView?: boolean;
	districts: any[];
	schemeId?: number;
	id?: string;
	initialData?: any;
};

const getFieldLabelAndStep = (fieldName: string) => {
	if (fieldName.startsWith("subcat_")) {
		return `[Observations] ${fieldName.replace("_remarks", " (Remarks)")}`;
	}

	const step1Labels: Record<string, string> = {
		district_id: "District",
		udise_code: "UDISE Code",
		school_name: "School Name",
		overall_quality_food: "Overall Quality of Food",
		avg_current_year_children_prepared: "Avg Children Prepared (Current Year)",
		avg_current_year_children_consumed: "Avg Children Consumed (Current Year)",
		avg_current_year_eggs_consumed: "Avg Eggs Consumed (Current Year)",
		avg_current_year_rice_cooked: "Avg Rice Cooked (Current Year)",
		avg_audit_period_children_prepared: "Avg Children Prepared (Audit Period)",
		avg_audit_period_children_consumed: "Avg Children Consumed (Audit Period)",
		avg_audit_period_eggs_consumed: "Avg Eggs Consumed (Audit Period)",
		avg_audit_period_rice_cooked: "Avg Rice Cooked (Audit Period)",
		unique_aspects: "Unique Aspects",
		committee_remarks: "Committee Remarks",
		changes_due_to_audit: "Changes due to Audit",
	};

	return `[Step 1] ${step1Labels[fieldName] || fieldName}`;
};

const FormObserver = () => {
	const { submitCount, isValid, isSubmitting, errors } = useFormikContext();

	useEffect(() => {
		if (submitCount > 0 && !isValid && !isSubmitting) {
			const errorKeys = Object.keys(errors);
			let errorFields = errorKeys
				.slice(0, 3)
				.map((key) => getFieldLabelAndStep(key))
				.join(" | ");
			if (errorKeys.length > 3) {
				errorFields += ` (+${errorKeys.length - 3} more)`;
			}
			toast.error(`Validation failed. Please check: ${errorFields}`);
		}
	}, [submitCount, isValid, isSubmitting, errors]);

	return null;
};

// Counts how many of a classification's sub-categories have any Observation
// Status picked, out of its total sub-category count — drives the
// "answered/total" fraction badge in the stepper nav.
const classificationProgress = (
	classification: NMPClassification,
	values: Record<string, any>,
) => {
	let answered = 0;
	let total = 0;
	classification.categories.forEach((category) => {
		category.sub_categories.forEach((sub) => {
			total += 1;
			if (values[`subcat_${sub.id}`]) answered += 1;
		});
	});
	return { answered, total };
};

const NMPSchemeStepper: React.FC<Props> = ({
	isView = false,
	districts,
	schemeId,
	id,
	initialData,
}) => {
	const location = useLocation();
	const [currentStep, setCurrentStep] = useState(location.state?.step || 0);
	const [currentId, setCurrentId] = useState<string | undefined>(id);
	const navigate = useNavigate();
	const submitMutation = useSchemeSubmit();
	const updateMutation = useSchemeUpdate();

	const { classifications } = useNMPClassificationTree(schemeId);

	// Step 0 = Basic Detail, steps 1..classifications.length = one per
	// classification with data for this scheme, last step = final submit.
	// The step count is fully driven by however many classifications the
	// scheme's mapped data currently produces — nothing hardcoded.
	const lastStepIndex = classifications.length + 1;

	const dynamicObservationsSchema = generateClassificationSchema(classifications);

	const fullSchema = NMPSchemaStep1.concat(dynamicObservationsSchema as any).concat(
		NMPSchemaStep6 as any,
	);

	const getCurrentStepSchema = () => {
		if (currentStep === 0) {
			return NMPSchemaStep1;
		}
		if (currentStep > 0 && currentStep <= classifications.length) {
			const currentClassification = classifications[currentStep - 1];
			if (currentClassification) {
				const stepQuestions = flattenClassificationTree([currentClassification]);
				const isAuditPara =
					currentClassification.name?.trim().toLowerCase() === "audit para";
				return Yup.object().shape(generateYupShape(stepQuestions, isAuditPara));
			}
			return Yup.object().shape({});
		}
		if (currentStep === lastStepIndex) {
			return fullSchema;
		}
		return Yup.object().shape({});
	};

	const currentSchema = getCurrentStepSchema();

	const handlePrev = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 0));
	};

	const onSubmit = async (values: any, actions: any) => {
		try {
			const dataToSave = { ...values, scheme_id: schemeId };
			let newId = currentId;

			if (currentId) {
				await updateMutation.mutateAsync({ id: currentId, data: dataToSave });
			} else {
				const res = await submitMutation.mutateAsync(dataToSave);
				if (res?.data?.id) {
					newId = res.data.id;
					setCurrentId(newId);
				}
			}

			if (currentStep === lastStepIndex) {
				toast.success("NMP Report submitted successfully");
				navigate("/scheme-data-entry");
			} else {
				toast.success(`Step ${currentStep + 1} saved successfully`);
				actions.resetForm({ values });
				if (!currentId && newId) {
					navigate(`/scheme-data-entry/edit/${newId}`, {
						replace: true,
						state: { step: currentStep + 1 },
					});
				} else {
					setCurrentStep(currentStep + 1);
				}
			}
		} catch (error: any) {
			console.error("Submission error:", error);
			const errorMessage =
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				"Failed to save progress";
			toast.warning(`${errorMessage} (Advancing to next step for testing)`);
			if (currentStep < lastStepIndex) {
				setCurrentStep(currentStep + 1);
			}
		} finally {
			actions.setSubmitting(false);
		}
	};

	return (
		<div className="card shadow-sm mb-5">
			<div className="card-header border-0 pt-5">
				<h3 className="card-title align-items-start flex-column">
					<span className="card-label fw-bold fs-3 mb-1">NMP Data Entry</span>
				</h3>
			</div>
			<div className="card-body py-3">
				<Formik
					initialValues={{
						...initialNMPValues,
						// dynamicObservationsInitialValues is intentionally NOT spread
						// here: its keys only exist once the classification tree
						// finishes loading, so including it would change the content
						// of `initialValues` after mount. With enableReinitialize,
						// Formik treats that as new data and resets the whole form —
						// wiping out anything set before the tree loaded (e.g. the
						// login-derived BRP Details below). Sub-category fields work
						// fine without being pre-declared here; existing saved answers
						// still arrive via `initialData` below.
						...(initialData || {}),
					}}
					validationSchema={currentSchema}
					onSubmit={onSubmit}
					enableReinitialize={true}
				>
					{(formik) => (
						<Form className="form" id="kt_create_account_form">
							<FormObserver />

							{/* Stepper Navigation */}
							<NMPStepProgress
								steps={[
									{
										title: "Basic Detail",
										description: "School & Audit Details",
									},
									...classifications.map((classification) => {
										const { answered, total } = classificationProgress(
											classification,
											formik.values,
										);
										return {
											title: classification.name,
											description: `Checklist for NMP — ${classification.name}`,
											answered,
											total,
										};
									}),
									{
										title: "Audit Observations",
										description: "Remarks, Case Studies & Report",
									},
								]}
								currentStep={currentStep + 1}
								onStepClick={(index) => setCurrentStep(index)}
								isView={isView}
							/>

							<div className="py-lg-5 px-lg-15">
								<div className="current" data-kt-stepper-element="content">
									<div className="w-100">
										{currentStep === 0 && (
											<NMPStep1
												isView={isView}
												districts={districts}
												schemeId={schemeId}
												id={currentId}
											/>
										)}
										{currentStep > 0 &&
											currentStep <= classifications.length && (
												<NMPClassificationStep
													classification={classifications[currentStep - 1]}
													isView={isView}
												/>
											)}
										{currentStep === lastStepIndex && (
											<NMPStep6 isView={isView} />
										)}
									</div>
								</div>

								<div className="d-flex flex-stack pt-10">
									<div className="mr-2">
										<button
											onClick={handlePrev}
											type="button"
											className="btn btn-lg btn-light-primary me-3"
											data-kt-stepper-action="previous"
											disabled={currentStep === 0 || isView}
											style={{ display: currentStep === 0 ? "none" : "" }}
										>
											<i className="ki-duotone ki-arrow-left fs-4 me-1"></i>{" "}
											Previous
										</button>
									</div>

									<div>
										{currentStep < lastStepIndex ? (
											<button
												type={isView ? "button" : "submit"}
												onClick={() => {
													if (isView) {
														setCurrentStep(currentStep + 1);
													}
												}}
												className="btn btn-lg btn-primary fw-bolder shadow-sm px-8"
												disabled={!isView && formik.isSubmitting}
											>
												{isView ? "Next" : "Save & Continue"}{" "}
												<i className="bi bi-arrow-right fs-4 ms-2"></i>
											</button>
										) : (
											!isView && (
												<button
													type="submit"
													className="btn btn-lg btn-success fw-bolder shadow-sm px-8"
													disabled={formik.isSubmitting}
												>
													Final Submit{" "}
													<i className="bi bi-check2-circle fs-4 ms-2"></i>
												</button>
											)
										)}
									</div>
								</div>
							</div>
						</Form>
					)}
				</Formik>
			</div>
		</div>
	);
};

export default NMPSchemeStepper;
